import {useContext, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {Delete} from '@mui/icons-material';
import {Box, IconButton, Typography} from '@mui/material';

import {DurationSelect} from '../DurationSelect';
import OnboardingSectionTitle from '../OnboardingSectionTitle';
import {ProfessionalAreasSelect} from '../ProfessionalAreasSelect';

import {EmpionButton} from '../../../../../components/EmpionButton/EmpionButton';
import {LanguageContext} from '../../../../../contexts/LanguageContext/LanguageContext';
import {orangeColors} from '../../../../../layouts/theme/designSystem';
import {DateOptions} from '../../../../../lib/constants';
import {WorkField} from '../../../../../types/MethodDepartmentCategory';
import {ProfessionalArea} from '../../../../../types/ProfessionalArea';
import {Duration, RequiredLanguageProfile} from '../../../../../types/shared-types';

interface AreasOfExpertiseProps {
  formik: any;
}

interface Payload {
  id: string;
  label: string;
  categoryName: RequiredLanguageProfile;
  duration: Duration;
  fieldsOfWork: WorkField[];
}

export default function AreasOfExpertise({formik}: AreasOfExpertiseProps) {
  const currentLanguage = useContext(LanguageContext);
  const {t} = useTranslation();

  const [professionalArea, setProfessionalArea] = useState<ProfessionalArea>();
  const [duration, setDuration] = useState<{unit: 'years' | 'months'; amount: string}>({unit: 'years', amount: '0'});
  const [selectedAreasOfExpertise, setSelectedAreasOfExpertise] = useState<Payload[]>([]);
  const handleAreaOfExpertiseSelection = (profArea: ProfessionalArea) => {
    if (
      formik.values.applicantOnboarding.qualifications.professionalExperiences.find(
        (record: any) => record.id === profArea.id
      )
    ) {
      const updatedAreasOfExpertise = formik.values.applicantOnboarding.qualifications.professionalExperiences.filter(
        (existingRecord: any) => existingRecord.id !== profArea.id
      );

      updatedAreasOfExpertise.push({
        methodDepartmentCategoryId: profArea.methodDepartmentCategoryId,
        label: profArea.label,
        duration: {
          amount: Number(duration.amount),
          unit: duration.unit,
        },
        fieldsOfWork: [],
      });

      formik.setFieldValue('applicantOnboarding.qualifications.professionalExperiences', updatedAreasOfExpertise);
    } else {
      formik.setFieldValue('applicantOnboarding.qualifications.professionalExperiences', [
        ...formik.values.applicantOnboarding.qualifications.professionalExperiences,
        {
          methodDepartmentCategoryId: profArea.methodDepartmentCategoryId,
          label: profArea.label,
          duration: {
            amount: Number(duration.amount),
            unit: duration.unit,
          },
          fieldsOfWork: [],
        },
      ]);
    }
    if (profArea) {
      setSelectedAreasOfExpertise([
        ...selectedAreasOfExpertise,
        {
          id: String(profArea.methodDepartmentCategoryId),
          label: profArea.label,
          categoryName: profArea.name,
          duration: {
            amount: Number(duration.amount),
            unit: duration.unit,
          },
          // For now fields of work shall be ignored.
          fieldsOfWork: [],
        },
      ]);
    }

    setProfessionalArea(undefined);
    setDuration({unit: 'years', amount: '0'});
  };

  const removeAreaOfExpertise = (id: string) => {
    const updatedAreasOfExpertise = formik.values.applicantOnboarding.qualifications.professionalExperiences.filter(
      (existingRecord: any) => existingRecord.id !== id
    );
    formik.setFieldValue('applicantOnboarding.qualifications.professionalExperiences', updatedAreasOfExpertise);
    setSelectedAreasOfExpertise([...selectedAreasOfExpertise.filter((entry) => entry.id !== id)]);
  };

  const formatExperienceText = (areaOfExpertise: Payload) => {
    const name = areaOfExpertise.categoryName[currentLanguage];
    const lengthOfTime = `${areaOfExpertise.duration.amount} ${
      DateOptions.find((option) => option.id === areaOfExpertise.duration.unit)?.label[currentLanguage]
    }`;
    const fieldNames: string[] = areaOfExpertise.fieldsOfWork.map((field) => field.name[currentLanguage]);

    return {
      title: `${name} (${lengthOfTime})`,
      fields: fieldNames.length ? `${fieldNames.join(', ')}` : '-',
    };
  };
  return (
    <Box id='qualifications'>
      <OnboardingSectionTitle title={t('applicantOnboarding.qualifications.title')} />
      <ProfessionalAreasSelect
        onChange={(professionalArea?: ProfessionalArea) => {
          setProfessionalArea(professionalArea);
        }}
        value={String(professionalArea?.id) || ''}
      />
      <Typography mt={3} mb={1} variant='h6'>
        {t('applicantOnboarding.qualifications.formLabels.yearsOfExperienceTitle')}
      </Typography>
      <DurationSelect onChange={setDuration} value={duration} />
      <Box display={'flex'} justifyContent={'flex-start'} mt={4}>
        {professionalArea ? (
          <EmpionButton
            disabled={!professionalArea || !duration.amount || duration.amount === '0'}
            onClick={() => {
              handleAreaOfExpertiseSelection(professionalArea);
            }}
          >
            {t('applicantOnboarding.qualifications.formLabels.addExperience')}
          </EmpionButton>
        ) : null}
      </Box>
      <Box mt={3}>
        <Typography mb={1} variant='h4'>
          {t('applicantOnboarding.qualifications.formLabels.experiencesListTitle')}
        </Typography>
        {selectedAreasOfExpertise.map((professionalExperience: Payload) => (
          <Box
            key={professionalExperience.id}
            display={'flex'}
            justifyContent={'space-between'}
            alignItems={'center'}
            alignSelf={'stretch'}
            sx={{
              mt: 1,
              backgroundColor: 'background.default',
              borderRadius: '0px 4px 4px 0px',
              borderLeft: `4px solid var(--orange, ${orangeColors[900]})`,
              p: 2,
            }}
          >
            <Box display='flex' flexDirection='column' justifyContent='space-around'>
              <Typography mb={1} variant='body1'>
                {formatExperienceText(professionalExperience).title}
              </Typography>
            </Box>
            <IconButton
              onClick={() => {
                removeAreaOfExpertise(professionalExperience.id);
              }}
            >
              <Delete />
            </IconButton>
          </Box>
        ))}
      </Box>
      <Typography variant='body2' color='error'>
        {formik?.errors?.applicantOnboarding?.qualifications?.areasOfExpertise || ''}
      </Typography>
    </Box>
  );
}
